/* eslint-disable react/prop-types */
/* eslint-disable import/no-unresolved, max-len */
import './Gallery.scss';

import { useState, useEffect, useMemo } from 'react';
import { Picture, Icon } from '@bwoty-web/ui-kit';
import { UI_PLAY } from '@bwoty-web/ui-kit/icons';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';
import cx from 'classnames';
import PropTypes from 'prop-types';

import PhotoAlbum from 'react-photo-album';
import Lightbox, { ImageSlide } from 'yet-another-react-lightbox';

// import optional lightbox styles
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

// import optional lightbox plugins
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';

import YoutubePlayer from './YoutubePlayer';
import PictureWrapper from './PictureWrapper';
import GalleryHead from './galleryHead/GalleryHead';
import GlobeDarkmodeButton from './darkmodeButton/GlobeDarkmodeButton';

import { imageSectionsArray } from '../../views/hotelInfo/sections/helper';

const Gallery = ({
  close,
  images,
  heading,
  galleryCategories,
  preselectedCategory,
  setPreselectedCategory,
  siteId,
  isAppWebView = false,
}) => {
  const isGlobetrotter = useIsGlobetrotter();
  const isMobile = useIsMobile();
  const skipGallery = images.length < 10;
  const prefersDarkmode =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  const [darkmode, setDarkmode] = useState(
    isGlobetrotter ? true : prefersDarkmode,
  );

  const [index, setIndex] = useState(skipGallery ? 0 : -1);
  const [design, setDesign] = useState(isGlobetrotter ? 4 : 1);
  const [layout, setLayout] = useState(
    isGlobetrotter && isMobile ? 'columns' : 'rows',
  );
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    document.body.classList.add('overflow-hidden');

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  });

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && index === -1) close();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [close, index]);

  const sharedPAStyle = {
    position: 'relative',
    backgroundColor: '#fff',
    boxShadow:
      design === 2
        ? '0px 3px 6px 0px rgba(0, 0, 0, 0.10), 0px 11px 11px 0px rgba(0, 0, 0, 0.09), 0px 25px 15px 0px rgba(0, 0, 0, 0.05), 0px 45px 18px 0px rgba(0, 0, 0, 0.01), 0px 71px 20px 0px rgba(0, 0, 0, 0.00)'
        : 'none',
  };

  const plugins = [Counter, Captions];
  const isYoutubeSlide = (slide) =>
    slide.type === 'youtube' || slide.type === 'video';
  const youtubeSrc = (url) =>
    `https://i.ytimg.com/vi_webp/${url}/mqdefault.webp`;

  const photoAlbumImages = useMemo(() => {
    const albumImages = [];

    images.forEach((image) => {
      albumImages.push({
        type: image.type,
        sources: image.splashSources,
        src: image.src || youtubeSrc(image.url),
        width: image.width || 300,
        height: image.height || image.width || 200,
        alt: image.alt || '',
        url: image.url || '',
        categories: image.categories || [],
      });
    });

    return albumImages;
  }, [images]);

  const yarlImages = useMemo(() => {
    const lightboxImages = [];

    images.forEach((image) => {
      lightboxImages.push({
        type: image.type,
        thumbnail: image.thumbnail,
        src: image.src || '',
        width: image.width || '',
        height: image.height || '',
        alt: image.alt,
        description: image.text,
        sources: image.sources || '',
        url: image.url || '',
        siteId,
        categories: image.categories || [],
      });
    });

    return lightboxImages;
  }, [images, siteId]);

  const imageCategories = useMemo(() => {
    const categories = [{ key: 'all', amount: yarlImages.length }];

    imageSectionsArray.forEach((section) => {
      section.includeCategories.forEach((category) => {
        yarlImages.forEach((image, ix) => {
          if (image.categories.length === 0) return;
          if (image.categories.includes(category)) {
            const catIndex = categories.findIndex(
              (el) => el.key === section.key,
            );
            if (catIndex === -1) {
              categories.push({ key: section.key, amount: 1, pictures: [ix] });
            }
            if (
              catIndex !== -1 &&
              !categories[catIndex]?.pictures.includes(ix)
            ) {
              categories[catIndex].amount += 1;
              categories[catIndex].pictures.push(ix);
            }
          }
        });
      });
    });

    return categories;
  }, [yarlImages]);

  const [albumImages, setAlbumImages] = useState(photoAlbumImages);
  const [lightboxImages, setLightboxImages] = useState(yarlImages);

  if (!isMobile && !isGlobetrotter) {
    plugins.splice(0, 0, Fullscreen);
    plugins.push(Thumbnails);
  }
  if (!isGlobetrotter) plugins.push(Zoom);

  const updateLayout = (layoutType) => {
    switch (layoutType) {
      case 1:
        setDesign(1);
        setLayout('rows');
        return;
      case 2:
        setDesign(2);
        setLayout('rows');
        return;
      case 3:
        setDesign(3);
        setLayout('columns');
        return;
      default:
        setDesign(1);
        setLayout('rows');
    }
  };
  const calculatePadding = (containerWidth) => {
    switch (design) {
      case 2:
        if (containerWidth < 416) return 12;
        if (containerWidth < 768) return 24;
        return 30;
      default:
        return 0;
    }
  };
  const calculateSpacing = (containerWidth) => {
    switch (design) {
      case 2:
        if (containerWidth < 416) return 12;
        if (containerWidth < 768) return 24;
        return 30;
      case 4:
        if (containerWidth < 768) return 24;
        return 48;
      default:
        if (containerWidth < 416) return 4;
        if (containerWidth < 768) return 8;
        return 12;
    }
  };
  const calculateRowHeight = (containerWidth) => {
    switch (design) {
      case 2:
        if (containerWidth < 416) return 120;
        if (containerWidth < 768) return 155;
        if (containerWidth < 1280) return 200;
        return 250;
      case 4:
        if (containerWidth < 768) return 185;
        return 310;
      default:
        if (containerWidth < 416) return 155;
        if (containerWidth < 768) return 200;
        if (containerWidth < 1280) return 220;
        return 250;
    }
  };

  // Enforce layout 3 if very few pictures (better visual experience)
  useEffect(() => {
    if (lightboxImages?.length < 3) updateLayout(3);
  }, [lightboxImages]);

  const hideGalleryInApp = index >= 0 && isAppWebView;

  return (
    <div
      className={cx('gw-gallery', {
        'gw-gallery--globe': isGlobetrotter,
        'gw-gallery--withCategories': imageCategories.length > 2,
        'gw-gallery--in-web-view': isAppWebView,
      })}
    >
      {!isAppWebView && (
        <GalleryHead
          heading={heading}
          sectionNames={galleryCategories}
          close={close}
          allAlbumImages={photoAlbumImages}
          setAlbumImages={setAlbumImages}
          allLightboxImages={yarlImages}
          setLightboxImages={setLightboxImages}
          design={design}
          updateLayout={updateLayout}
          imageCategories={imageCategories}
          preselectedCategory={preselectedCategory}
          setPreselectedCategory={setPreselectedCategory}
          darkmode={darkmode}
          setDarkmode={setDarkmode}
        />
      )}
      <div className="gw-gallery__content">
        {!skipGallery && !hideGalleryInApp && (
          <PhotoAlbum
            photos={albumImages}
            layout={layout}
            columns={(containerWidth) => {
              if (containerWidth < 416) return 2;
              if (containerWidth < 768) return 3;
              if (containerWidth < 1280) return 4;
              return 5;
            }}
            spacing={(containerWidth) => calculateSpacing(containerWidth)}
            padding={(containerWidth) => calculatePadding(containerWidth)}
            targetRowHeight={(containerWidth) =>
              calculateRowHeight(containerWidth)
            }
            onClick={({ index: current }) => setIndex(current)}
            renderPhoto={({
              photo,
              wrapperStyle,
              renderDefaultPhoto,
              imageProps,
            }) =>
              isYoutubeSlide(photo) ? (
                <div
                  className="gw-gallery-album-item"
                  style={{ ...wrapperStyle, ...sharedPAStyle }}
                >
                  {renderDefaultPhoto({ wrapped: true })}
                  <Icon
                    className="gw-gallery__content-play-icon"
                    path={UI_PLAY}
                    size="l"
                  />
                </div>
              ) : (
                <div
                  {...imageProps}
                  className="gw-gallery-album-item"
                  style={{ ...wrapperStyle, ...sharedPAStyle }}
                >
                  <Picture alt={photo.alt} sources={photo.sources} />
                </div>
              )
            }
          />
        )}
        <Lightbox
          index={index}
          slides={lightboxImages}
          className={cx({
            'gw-gallery-lightbox--globe': isGlobetrotter,
            'gw-gallery-lightbox--in-web-view': isAppWebView,
          })}
          open={index >= 0}
          close={skipGallery ? close : () => setIndex(-1)}
          plugins={plugins}
          toolbar={
            isGlobetrotter
              ? {
                  buttons: [
                    <GlobeDarkmodeButton
                      key={0}
                      checked={darkmode}
                      setDarkmode={setDarkmode}
                    />,
                    'close',
                  ],
                }
              : undefined
          }
          render={{
            slide: ({ slide }) =>
              isYoutubeSlide(slide) ? (
                <YoutubePlayer
                  slide={slide}
                  index={index}
                  playing={playing}
                  setPlaying={setPlaying}
                  siteId={siteId}
                />
              ) : (
                <PictureWrapper sources={slide.sources}>
                  <ImageSlide slide={{ src: slide.src }} />
                </PictureWrapper>
              ),
            thumbnail: ({ slide, onClick, ...rest }) =>
              isYoutubeSlide(slide) ? (
                <div style={{ position: 'relative' }}>
                  <ImageSlide slide={{ src: slide.thumbnail }} {...rest} />
                  <Icon
                    className="gw-gallery__content-play-icon"
                    path={UI_PLAY}
                    size="m"
                  />
                </div>
              ) : (
                <ImageSlide slide={{ src: slide.thumbnail }} {...rest} />
              ),
            buttonZoom: isMobile ? () => null : undefined,
          }}
          on={{
            view: ({ index: currentIndex }) => {
              setIndex(currentIndex);
              if (playing) setPlaying(false);
            },
          }}
          zoom={{ maxZoomPixelRatio: 2 }}
          captions={{ descriptionTextAlign: 'center' }}
          thumbnails={{
            border: 0,
            borderRadius: 0,
            padding: 0,
            gap: 8,
          }}
        />
      </div>
    </div>
  );
};

Gallery.propTypes = {
  close: PropTypes.func,
  preselectedCategory: PropTypes.string,
  setPreselectedCategory: PropTypes.func,
  images: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  heading: PropTypes.string,
  galleryCategories: PropTypes.shape({}),
  siteId: PropTypes.number.isRequired,
};

Gallery.defaultProps = {
  close: undefined,
  heading: '',
  preselectedCategory: 'all',
  setPreselectedCategory: () => null,
  galleryCategories: {},
};

export default Gallery;
